import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const COLORS = ["#2196f3", "#e91e63", "#4caf50", "#ff9800", "#9c27b0", "#f44336"];

const GeneralChartsSection = ({ chartData = [] }) => {
  // Group data by chartType
  const groupedData = chartData.reduce((acc, item) => {
    if (!acc[item.chartType]) {
      acc[item.chartType] = [];
    }
    acc[item.chartType].push(item);
    return acc;
  }, {});

  // Chart configurations
  const chartConfig = {
    gender: {
      title: "Employment by Gender",
      dataKey: "value",
      labelField: "label",
    },
    studyLocation: {
      title: "Employment by Study Location",
      dataKey: "value",
      labelField: "label",
    },
    youthByGender: {
      title: "Youth Employment by Gender",
      dataKey: "value",
      labelField: "label",
    },
    youthSummary: {
      title: "Youth vs All Employment",
      dataKey: "value",
      labelField: "label",
    },
  };

  const renderPieChart = (chartType, data) => {
    const config = chartConfig[chartType];
    if (!config || !data || data.length === 0) return null;

    const totalValue = data.reduce((sum, d) => sum + d.value, 0);

    return (
      <Grid item xs={12} md={3} key={chartType}>
        <Card sx={{ height: 500, overflow: "auto" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {config.title}
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey={config.dataKey}
                  label={({ percent, label }) => 
                    `${label}: ${(percent * 100).toFixed(1)}%`
                  }
                  labelLine={true}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => {
                    const payload = props.payload;
                    const percent = payload.valuePercent || ((value / totalValue) * 100);
                    return [
                      `${value.toLocaleString()} (${percent.toFixed(1)}%)`,
                      "Count",
                    ];
                  }}
                  labelFormatter={(label, props) =>
                    `${props[0]?.payload.label}`
                  }
                />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Breakdown:
              </Typography>
              {data.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: index > 0 ? 1 : 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        bgcolor: COLORS[index % COLORS.length],
                        borderRadius: "50%",
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2">
                      {item.label}:{" "}
                      <strong>
                        {item.valuePercent
                          ? `${item.valuePercent}%`
                          : `${((item.value / totalValue) * 100).toFixed(2)}%`}
                      </strong>{" "}
                      ({item.value.toLocaleString()})
                    </Typography>
                  </Box>
                </Box>
              ))}
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Total: {totalValue.toLocaleString()}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {Object.keys(chartConfig).map((chartType) =>
        renderPieChart(chartType, groupedData[chartType])
      )}
    </Grid>
  );
};

export default GeneralChartsSection;

